import { createSelector } from 'reselect';
import _get from 'lodash/get';
import { isEmpty } from '../common/utils/data-utils';
import {
  POSTPAID_BILLING_TYPE,
  SERVICE_TYPE,
} from '../constants/booking-constants';
import { userInfoSelector } from './app-selector';

const arrayToMap = array => {
  if (!array) return {};

  const result = {};

  array.forEach(item => {
    const key = JSON.stringify({
      latitude: item.latlng.latitude,
      longitude: item.latlng.longitude,
    });

    result[key] = item;
  });

  return result;
};

const bookingSelector = state => state.booking;

export const transformServiceTypes = (rawServiceTypes, paymentMethods) => {
  const {
    categoriesMap,
    groupsMap,
    servicesMap,
    servicesConfig = {},
  } = rawServiceTypes;
  if (isEmpty(servicesMap)) return [];

  const deliveryCategoryIDs = Object.keys(categoriesMap).filter(
    categoryID =>
      categoriesMap[categoryID].name === 'Delivery' ||
      categoriesMap[categoryID].name === 'Rental',
  );
  const groupIDs = Object.keys(groupsMap).reduce(
    (acc, groupID) =>
      deliveryCategoryIDs.includes(String(groupsMap[groupID].categoryID))
        ? Object.assign({}, acc, { [groupID]: groupID })
        : acc,
    {},
  );
  const serviceTypes = Object.keys(servicesMap).reduce((acc, serviceID) => {
    const serviceEntity = servicesMap[serviceID];
    const serviceConfigurationEntity = servicesConfig[serviceID]
      ? servicesConfig[serviceID]
      : {};
    if (!groupIDs[serviceEntity.groupID] || !serviceEntity.activated) {
      return acc;
    }
    const allowCash = serviceEntity.paymentMethods.indexOf('CASH') !== -1;
    const allowCard = serviceEntity.paymentMethods.indexOf('CARD') !== -1;
    const allowGPCOnly =
      serviceEntity.paymentMethods.length === 2 &&
      serviceEntity.paymentMethods.indexOf('CARD') !== -1 &&
      serviceEntity.paymentMethods.indexOf('GPCONLY') !== -1;
    if (allowGPCOnly || (!allowCash && !allowCard)) {
      return acc;
    }

    const maxMSDStops = _get(
      serviceEntity,
      'configuration["grabexpress.service.MultiStopMaxStopCount"].value',
      1,
    );

    const maxWeight = _get(
      serviceEntity,
      'configuration["grabexpress.service.MaxWeight"].value',
      1,
    );

    const taxiTypeInfo = _get(
      serviceEntity,
      'configuration["web.data.HTML"].value',
      undefined,
    );

    return acc.concat({
      ID: serviceEntity.ID,
      name: serviceEntity.name,
      expressServiceID:
        serviceEntity.configuration['grabexpress.service.ExpressServiceID']
          .value,
      iconName: serviceEntity.configuration['paxapp.ui.IconLink'].value,
      allowCash,
      allowCard,
      windowTime: serviceConfigurationEntity.windowTime || '',
      maxMSDStops,
      maxWeight,
      taxiTypeInfo,
      cashOnDelivery:
        serviceEntity.configuration['grabexpress.service.CashOnDelivery'].value,
    });
  }, []);
  serviceTypes.sort((a, b) => {
    let weightA = 0;
    let weightB = 0;

    if (a.allowCash && a.allowCard) weightA = 20;
    else if (a.allowCard) weightA = !isEmpty(paymentMethods) ? 10 : 5;
    else weightA = !isEmpty(paymentMethods) ? 5 : 10;

    if (b.allowCash && b.allowCard) weightB = 20;
    else if (b.allowCard) weightB = !isEmpty(paymentMethods) ? 10 : 5;
    else weightB = !isEmpty(paymentMethods) ? 5 : 10;

    return weightB - weightA;
  });
  return serviceTypes;
};

export const windowTimeSelector = createSelector(bookingSelector, booking => {
  if (
    isEmpty(booking.serviceTypes) ||
    isEmpty(booking.serviceTypes.servicesMap)
  ) {
    return {};
  }
  const listServiceTypes = booking.serviceTypes.servicesMap;
  const { servicesConfig } = booking.serviceTypes;
  let sameDayService;
  Object.keys(listServiceTypes).forEach(serviceID => {
    const serviceConfigurationEntity = servicesConfig[serviceID]
      ? servicesConfig[serviceID]
      : {};
    const expressServiceID =
      listServiceTypes[serviceID].configuration[
        'grabexpress.service.ExpressServiceID'
      ].value;
    if (expressServiceID === SERVICE_TYPE.SAMEDAY) {
      sameDayService = {
        ...listServiceTypes[serviceID],
        windowTime: serviceConfigurationEntity.windowTime || '',
      };
    }
  });
  if (!isEmpty(sameDayService)) {
    const { windowTime } = sameDayService;
    return windowTime;
  }
  return {};
});

export const serviceTypeSelector = createSelector(bookingSelector, booking => {
  if (isEmpty(booking.serviceTypes)) return [];
  return transformServiceTypes(booking.serviceTypes, booking.paymentMethods);
});

export const paymentMethodSelector = createSelector(
  bookingSelector,
  booking => (booking.paymentMethods && booking.paymentMethods.result) || [],
);

export const serviceTypesNotAvailableSelector = createSelector(
  bookingSelector,
  booking => booking.serviceTypesNotAvailable,
);

export const allowPostpaidBillingSelector = createSelector(
  bookingSelector,
  booking =>
    !isEmpty(booking.paymentMethods)
      ? booking.paymentMethods.result.findIndex(
          payment => payment.type === POSTPAID_BILLING_TYPE,
        ) !== -1
      : false,
);

export const promoStateSelector = createSelector(
  bookingSelector,
  booking => booking.promoState,
);

export const addressSearchSelector = createSelector(
  bookingSelector,
  booking => booking.addressSearch,
);

export const addressTranslateSelector = createSelector(
  bookingSelector,
  booking => booking.addressTranslate,
);

export const addressPoolMapSelector = createSelector(
  bookingSelector,
  booking => {
    const { addressTranslate, addressSearch } = booking;

    return {
      ...arrayToMap(addressSearch.result, 'latlng'),
      ...arrayToMap(addressSearch.extra, 'latlng'),
      ...arrayToMap(addressTranslate.result, 'latlng'),
      ...arrayToMap(addressTranslate.extra, 'latlng'),
    };
  },
);

export const quotesSelector = createSelector(
  bookingSelector,
  booking => booking.quotes,
);

export const promoQuotesSelector = createSelector(
  bookingSelector,
  booking => booking.promoQuotes,
);

export const createdOrdersSelector = createSelector(
  bookingSelector,
  booking => booking.createdOrders,
);

export const bulkUploadProcessingSelector = createSelector(
  bookingSelector,
  booking => booking.bulkUploadProcessing,
);

export const paymentMethodsSelector = createSelector(bookingSelector, booking =>
  _get(booking, 'paymentMethods.result', []),
);

export const serviceTypesInCitySelector = createSelector(
  userInfoSelector,
  bookingSelector,
  (userInfo, booking) => {
    const cityID = _get(userInfo, ['selectedCity', 'cityID']);
    if (!cityID) {
      return [];
    }
    return _get(booking.serviceTypeMap, cityID, []);
  },
);
